<app-modal-shell (dismiss)="dismiss()">
  <div modalTitle class="support-workspace__title">
    <h2>{{ 'supportWorkspace.title' | translate }}</h2>
    <p>{{ 'supportWorkspace.description' | translate }}</p>
  </div>

  <div class="support-workspace">
    <aside class="support-workspace__sidebar">
      <div class="support-workspace__sidebar-header">
        <div>
          <h3>{{ 'supportWorkspace.sidebar.title' | translate }}</h3>
          <p>{{ 'supportWorkspace.sidebar.description' | translate }}</p>
        </div>
        @if (canManage()) {
          <div class="support-workspace__sidebar-actions">
            <button class="support-workspace__ghost-button"
              type="button"
              (click)="openCreate('category')">
              + {{ 'supportWorkspace.actions.addCategory' | translate }}
            </button>
            <button class="support-workspace__ghost-button"
              type="button"
              (click)="openCreate('article')">
              + {{ 'supportWorkspace.actions.addArticle' | translate }}
            </button>
          </div>
        }
      </div>

      <div class="support-workspace__tree">
        @for (row of navRows(); track row.item.guid) {
          <div class="support-tree-row"
            [class.support-tree-row--active]="selectedGuid() === row.item.guid"
            [style.paddingLeft.px]="14 + row.depth * 18"
            (click)="selectItem(row.item)">
            <div class="support-tree-row__main">
              <ion-icon [name]="row.item.type === 'category' ? 'folder-open-outline' : 'document-text-outline'"></ion-icon>
              <span>{{ row.item.title }}</span>
            </div>

            @if (canManage()) {
              <div class="support-tree-row__actions">
                <button type="button"
                  class="support-tree-row__icon"
                  (click)="$event.stopPropagation(); openEdit(row.item)">
                  <ion-icon name="create-outline"></ion-icon>
                </button>
                <button type="button"
                  class="support-tree-row__icon support-tree-row__icon--danger"
                  (click)="$event.stopPropagation(); deleteItem(row.item)">
                  <ion-icon name="trash-outline"></ion-icon>
                </button>
              </div>
            }
          </div>
        } @empty {
          <div class="support-workspace__sidebar-empty">
            <strong>{{ 'supportWorkspace.sidebar.emptyTitle' | translate }}</strong>
            <p>{{ 'supportWorkspace.sidebar.emptyDescription' | translate }}</p>
          </div>
        }
      </div>
    </aside>

    <section class="support-workspace__main">
      @if (editorMode()) {
        <div class="support-editor">
          <div class="support-editor__header">
            <div>
              <p class="support-editor__eyebrow">{{ editorMode() === 'create' ? ('supportWorkspace.editor.createEyebrow' | translate) : ('supportWorkspace.editor.editEyebrow' | translate) }}</p>
              <h3>{{ editorType() === 'category' ? ('supportWorkspace.labels.category' | translate) : ('supportWorkspace.labels.article' | translate) }}</h3>
            </div>
            <div class="support-editor__actions">
              <button class="support-workspace__ghost-button"
                type="button"
                (click)="cancelEdit()">
                {{ 'general.cancel' | translate }}
              </button>
              <button class="support-workspace__primary-button"
                type="button"
                (click)="saveForm()">
                {{ 'general.save' | translate }}
              </button>
            </div>
          </div>

          <form class="support-editor__form" [formGroup]="formGroup">
            <label class="support-editor__field support-editor__field--full">
              <span>{{ 'supportWorkspace.fields.title' | translate }}</span>
              <input type="text" formControlName="title" [placeholder]="'supportWorkspace.placeholders.title' | translate" />
            </label>

            <label class="support-editor__field">
              <span>{{ 'supportWorkspace.fields.type' | translate }}</span>
              <app-select-popover formControlName="type" (change)="editorType.set($any($event.target).value)">
                <ion-select-option value="category">{{ 'supportWorkspace.labels.category' | translate }}</ion-select-option>
                <ion-select-option value="article">{{ 'supportWorkspace.labels.article' | translate }}</ion-select-option>
              </app-select-popover>
            </label>

            <label class="support-editor__field">
              <span>{{ 'supportWorkspace.fields.parent' | translate }}</span>
              <app-select-popover formControlName="parentGuid">
                <ion-select-option [value]="null">{{ 'supportWorkspace.fields.root' | translate }}</ion-select-option>
                @for (row of categoryRows(); track row.item.guid) {
                  <ion-select-option [value]="row.item.guid">{{ '— '.repeat(row.depth) }}{{ row.item.title }}</ion-select-option>
                }
              </app-select-popover>
            </label>

            @if ((formGroup.get('type')?.value ?? editorType()) === 'article') {
              <label class="support-editor__field support-editor__field--full">
                <span>{{ 'supportWorkspace.fields.content' | translate }}</span>
                <quill-editor class="support-editor__quill"
                  formControlName="content"
                  [modules]="modules()"
                  [placeholder]="'supportWorkspace.placeholders.content' | translate"
                  (onEditorCreated)="getEditorInstance($event)">
                </quill-editor>
              </label>
            } @else {
              <div class="support-editor__hint support-editor__field--full">
                {{ 'supportWorkspace.editor.categoryHint' | translate }}
              </div>
            }
          </form>
        </div>
      } @else if (selectedItem(); as item) {
        @if (item.type === 'article') {
          <article class="support-article">
            <div class="support-article__header">
              <div>
                <div class="support-article__breadcrumb">
                  @for (crumb of breadcrumb(); track crumb.guid; let last = $last) {
                    <span>{{ crumb.title }}</span>
                    @if (!last) {
                      <ion-icon name="chevron-forward-outline"></ion-icon>
                    }
                  }
                </div>
                <h2>{{ item.title }}</h2>
                <p>{{ getParentTitle(item.parentGuid) }}</p>
              </div>

              @if (canManage()) {
                <div class="support-article__actions">
                  <button class="support-workspace__ghost-button"
                    type="button"
                    (click)="openEdit(item)">
                    {{ 'general.edit' | translate }}
                  </button>
                  <button class="support-workspace__ghost-button"
                    type="button"
                    (click)="openCreate('article', item.parentGuid)">
                    {{ 'supportWorkspace.actions.addSiblingArticle' | translate }}
                  </button>
                </div>
              }
            </div>

            @if (item.content?.length > 0) {
              <div class="support-article__content" [innerHTML]="item.content"></div>
            } @else {
              <div class="support-article__empty">
                {{ 'supportWorkspace.article.empty' | translate }}
              </div>
            }
          </article>
        } @else {
          <section class="support-category">
            <div class="support-category__header">
              <div>
                <p class="support-category__eyebrow">{{ 'supportWorkspace.labels.category' | translate }}</p>
                <h2>{{ item.title }}</h2>
                <p>{{ 'supportWorkspace.category.description' | translate }}</p>
              </div>

              @if (canManage()) {
                <div class="support-category__actions">
                  <button class="support-workspace__ghost-button"
                    type="button"
                    (click)="openCreate('category', item.guid)">
                    + {{ 'supportWorkspace.actions.addSubcategory' | translate }}
                  </button>
                  <button class="support-workspace__primary-button"
                    type="button"
                    (click)="openCreate('article', item.guid)">
                    + {{ 'supportWorkspace.actions.addArticle' | translate }}
                  </button>
                  <button class="support-workspace__ghost-button"
                    type="button"
                    (click)="openEdit(item)">
                    {{ 'general.edit' | translate }}
                  </button>
                </div>
              }
            </div>

            <div class="support-category__children">
              @for (child of selectedChildren(); track child.guid) {
                <button class="support-category__child"
                  type="button"
                  (click)="selectItem(child)">
                  <ion-icon [name]="child.type === 'category' ? 'folder-open-outline' : 'document-text-outline'"></ion-icon>
                  <div>
                    <strong>{{ child.title }}</strong>
                    <span>{{ child.type === 'category' ? ('supportWorkspace.labels.category' | translate) : ('supportWorkspace.labels.article' | translate) }}</span>
                  </div>
                </button>
              } @empty {
                <div class="support-article__empty">{{ 'supportWorkspace.category.empty' | translate }}</div>
              }
            </div>
          </section>
        }
      } @else {
        <section class="support-home">
          <div class="support-home__hero">
            <p class="support-home__eyebrow">{{ 'supportWorkspace.home.eyebrow' | translate }}</p>
            <h2>{{ 'supportWorkspace.title' | translate }}</h2>
            <p>{{ 'supportWorkspace.home.description' | translate }}</p>
          </div>

          <div class="support-home__search">
            <label>
              <span>{{ 'general.search' | translate }}</span>
              <input type="search"
                [value]="searchTerm()"
                [placeholder]="'supportWorkspace.home.searchPlaceholder' | translate"
                (input)="onSearch($any($event.target).value)" />
            </label>
          </div>

          @if (searchTerm().trim().length > 0) {
            <div class="support-home__results">
              @for (item of searchResults(); track item.guid) {
                <button class="support-home__result"
                  type="button"
                  (click)="openSearchResult(item)">
                  <div>
                    <strong>{{ item.title }}</strong>
                    <span>{{ item.type === 'category' ? ('supportWorkspace.labels.category' | translate) : getParentTitle(item.parentGuid) }}</span>
                  </div>
                  @if (item.type === 'article' && stripHtml(item.content).length > 0) {
                    <p>{{ stripHtml(item.content).slice(0, 140) }}@if (stripHtml(item.content).length > 140) { ... }</p>
                  }
                </button>
              } @empty {
                <div class="support-article__empty">{{ 'supportWorkspace.home.noResults' | translate }}</div>
              }
            </div>
          } @else {
            <div class="support-home__top-level">
              <h3>{{ 'supportWorkspace.home.topCategories' | translate }}</h3>
              <div class="support-home__top-grid">
                @for (row of introCategories(); track row.item.guid) {
                  <button class="support-home__top-card"
                    type="button"
                    (click)="selectItem(row.item)">
                    <ion-icon name="folder-open-outline"></ion-icon>
                    <strong>{{ row.item.title }}</strong>
                  </button>
                } @empty {
                  <div class="support-article__empty">{{ 'supportWorkspace.home.empty' | translate }}</div>
                }
              </div>
            </div>
          }
        </section>
      }
    </section>
  </div>
</app-modal-shell>
